import { remoteWebSocketUrl } from './pairing'
import { parseServerRemoteMessage, REMOTE_MESSAGE_MAX_BYTES, type ServerRemoteMessage } from './remoteProtocol'

export type RemoteSocketStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'unauthorized'

interface Options {
  deviceId: string
  role: 'tv' | 'controller'
  token: string
  onMessage: (message: ServerRemoteMessage) => void
  onStatus: (status: RemoteSocketStatus) => void
}

interface PendingMessage {
  raw: string
  expiresAt: number
}

/** Reconnecting WebSocket shared by the TV and phone. Authentication lives in
 * a subprotocol header, never in a URL that proxies and logs commonly retain. */
export class RemoteSocket {
  private socket: WebSocket | null = null
  private stopped = true
  private reconnectTimer = 0
  private pingTimer = 0
  private attempts = 0
  private pending: PendingMessage[] = []
  private wakeListening = false
  private retrying = false

  constructor(private options: Options) {}

  private wake = () => {
    if (this.stopped || !this.retrying || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return
    // A phone tab returning from background and a TV regaining Wi-Fi should
    // not sit through the tail of a 20-second exponential backoff.
    window.clearTimeout(this.reconnectTimer)
    this.attempts = 0
    this.retrying = false
    this.connect()
  }

  private listenForWake() {
    if (this.wakeListening) return
    this.wakeListening = true
    window.addEventListener('online', this.wake)
    document.addEventListener('visibilitychange', this.wake)
  }

  private stopListeningForWake() {
    if (!this.wakeListening) return
    this.wakeListening = false
    window.removeEventListener('online', this.wake)
    document.removeEventListener('visibilitychange', this.wake)
  }

  start() {
    if (!this.stopped) return
    this.stopped = false
    this.listenForWake()
    this.connect()
  }

  stop() {
    this.stopped = true
    this.retrying = false
    window.clearTimeout(this.reconnectTimer)
    window.clearInterval(this.pingTimer)
    this.socket?.close(1000, 'Client stopped')
    this.socket = null
    this.stopListeningForWake()
    this.options.onStatus('idle')
  }

  send(value: unknown, queueForMilliseconds = 30_000) {
    const raw = JSON.stringify(value)
    if (raw.length > REMOTE_MESSAGE_MAX_BYTES || new TextEncoder().encode(raw).byteLength > REMOTE_MESSAGE_MAX_BYTES) return false
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(raw)
      return true
    }
    if (queueForMilliseconds > 0) {
      this.pending.push({ raw, expiresAt: Date.now() + queueForMilliseconds })
      this.pending = this.pending.slice(-20)
    }
    return false
  }

  private connect() {
    if (this.stopped) return
    this.options.onStatus('connecting')
    let socket: WebSocket
    try {
      socket = new WebSocket(remoteWebSocketUrl(this.options.deviceId), [
        'vela-v1',
        `role-${this.options.role}`,
        `auth-${this.options.token}`,
      ])
    } catch {
      this.scheduleReconnect()
      return
    }
    this.socket = socket
    socket.onopen = () => {
      this.attempts = 0
      this.retrying = false
      this.options.onStatus('connected')
      const now = Date.now()
      this.pending.filter((entry) => entry.expiresAt > now).forEach((entry) => socket.send(entry.raw))
      this.pending = []
      window.clearInterval(this.pingTimer)
      this.pingTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send('ping')
      }, 20_000)
    }
    socket.onmessage = (event) => {
      if (event.data === 'pong') return
      const message = typeof event.data === 'string' ? parseServerRemoteMessage(event.data) : null
      if (message) this.options.onMessage(message)
    }
    socket.onerror = () => {
      // close supplies the retry path; browsers intentionally hide handshake details
    }
    socket.onclose = (event) => {
      if (this.socket !== socket) return
      this.socket = null
      window.clearInterval(this.pingTimer)
      if (this.stopped) return
      if (event.code === 4001 || event.code === 4002) {
        this.retrying = false
        this.options.onStatus('unauthorized')
        return
      }
      if (event.code === 4003) {
        this.retrying = false
        this.options.onStatus('disconnected')
        return
      }
      this.options.onStatus('disconnected')
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect() {
    if (this.stopped) return
    this.retrying = true
    const delays = [800, 1_500, 3_000, 6_000, 12_000, 20_000]
    const delay = delays[Math.min(this.attempts, delays.length - 1)]
    this.attempts += 1
    window.clearTimeout(this.reconnectTimer)
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay)
  }
}
